import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InfoPanel, Typography } from '@otcmarketsgroup/otcm-ui-lib';
import { reopenCuf, getApplication } from '../../api/cuf';
import { appStatuses, warnings } from '../../constants';
import { useSyncApplication } from '../../hooks/useSyncApplication';
import { testIds } from '../../constants/testIds';

const ReopenForm = ({ symbol, email, open, onOpenChange }) => {
    const [showDialog, setShowDialog] = useState(false);
    const queryClient = useQueryClient();
    const isControlled = typeof open === 'boolean';
    const dialogOpen = isControlled ? open : showDialog;

    const setDialogState = (nextOpen) => {
        if (!isControlled) {
            setShowDialog(nextOpen);
        }
        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    };

    useEffect(() => {
        return () => {
            queryClient.removeQueries({ queryKey: ['currentApplication', symbol, email] });
        };
    }, []);

    const handleCloseDialog = () => {
        if (reopenMutation.isPending) return;
        setDialogState(false);
    };

    const applicationQuery = useQuery({
        queryKey: ['application', symbol, email],
        queryFn: ({ signal }) => getApplication({ symbol, email }, signal),
        retry: false,
        enabled: false,
    });

    useSyncApplication({ ...applicationQuery?.data, status: { id: appStatuses.NO_APPLICATION } }, applicationQuery.isSuccess);

    useEffect(() => {
        if (applicationQuery.isSuccess) {
            setDialogState(false);
        }
    }, [applicationQuery.isSuccess]);

    const reopenMutation = useMutation({
        mutationFn: () => reopenCuf({ symbol, email }),
        onSuccess: () => {
            applicationQuery.refetch();
        },
        onError: (err) => {
            console.error(err);
        }
    });

    const isDataFetching = (reopenMutation.isPending || applicationQuery.isFetching);

    return (
        <>
            <Button
                variant="outlined"
                fullWidth
                data-testid={testIds.controlPanel.reopenBtn}
                onClick={() => setDialogState(true)}
            >
                Reopen
            </Button>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} data-testid={testIds.controlPanel.reopenDialog}>
                <DialogTitle title="Reopen Profile" onClose={handleCloseDialog} />
                <DialogContent>
                    {isDataFetching
                        ? <InfoPanel isWarning title={warnings.DO_NOT_REFRESH} />
                        : `Are you sure you want to Modify this Verified Profile?  If you proceed, the original submission will be recalled and no changes will be made to the company's information until a new Verified Profile is submitted`
                    }
                    {reopenMutation.isError && (
                        <Box mt={2}>
                            <InfoPanel isWarning title="Unable to reopen profile" />
                        </Box>
                    )}
                    {applicationQuery.isError && (
                        <Box mt={2}>
                            <InfoPanel isWarning title='Unable to fetch application. Refresh page.' />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        data-testid={testIds.controlPanel.reopenBackBtn}
                        disabled={isDataFetching}
                        onClick={handleCloseDialog}
                    >
                        No, go back
                    </Button>
                    <Button
                        variant="contained"
                        data-testid={testIds.controlPanel.reopenConfirmBtn}
                        isLoading={isDataFetching}
                        disabled={isDataFetching}
                        onClick={() => reopenMutation.mutate()}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

ReopenForm.propTypes = {
    symbol: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
};

export default ReopenForm;